import { formatDateOnlyShort } from '@/app/utils/formatDate';
import { formatMileage } from '@/features/vehiclesearch/utils/mileage';
import { BALANCE_LOADING_REASON, BALANCE_UNAVAILABLE_REASON } from './invoiceBalance';
import { isReversed } from './invoiceReversal';
import type { AgreementInvoice, AgreementInvoicesQuery } from '../types/agreements.types';

export type SummaryTileId = 'account_balance' | 'last_invoiced';

export interface SummaryTile {
    id: SummaryTileId;
    label: string;
    value: string;
    detail: string | null;
}

export const TILE_PLACEHOLDER = '—';

export function settledInvoices({
    data,
    isLoading,
    isError,
}: Pick<AgreementInvoicesQuery, 'data' | 'isLoading' | 'isError'>): AgreementInvoice[] | undefined {
    return isLoading || isError ? undefined : data;
}

export function accountBalanceTile(availableBalance: string | null, unreadableReason: string | null): SummaryTile {
    const base = { id: 'account_balance', label: 'Account balance', detail: 'Available to claim' } as const;
    if (unreadableReason === BALANCE_LOADING_REASON) return { ...base, value: 'Checking...' };
    if (unreadableReason === BALANCE_UNAVAILABLE_REASON || availableBalance === null) {
        return { ...base, value: 'Unavailable' };
    }
    return { ...base, value: availableBalance };
}

export function lastInvoicedTile(invoices: AgreementInvoice[] | undefined): SummaryTile {
    const base = { id: 'last_invoiced', label: 'Last invoiced' } as const;
    if (!invoices) return { ...base, value: TILE_PLACEHOLDER, detail: null };
    const latest = invoices.find((invoice) => !isReversed(invoice));
    if (!latest) return { ...base, value: 'Not yet invoiced', detail: null };

    return {
        ...base,
        value: formatDateOnlyShort(latest.invoicedAt),
        detail: latest.mileage === null ? null : `at ${formatMileage(latest.mileage)} miles`,
    };
}
